import {FormEvent, useState} from 'react';

import {MuiOtpInput} from 'mui-one-time-password-input';
import {useTranslation} from 'react-i18next';

import {Box, Container, Typography} from '@mui/material';

import {OTP_LENGTH, OTP_MAX_ATTEMPTS, useOtpVerification} from '../hooks/useOtpVerification';

import CopyrightLink from '../../../../components/CopyrightLink/CopyrightLink';
import {EmpionButton} from '../../../../components/EmpionButton/EmpionButton';
import {greenColors, orangeColors} from '../../../../layouts/theme/designSystem';

interface Props {
  authToken: string;
  email: string;
  onVerified: () => void;
  onBackToSignIn: () => void;
}

const isDigit = (character: string) => /^\d$/.test(character);

export const OtpVerificationForm = ({authToken, email, onVerified, onBackToSignIn}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'registration.twoFactor'});
  const [code, setCode] = useState('');

  const {
    attemptsLeft,
    codeResent,
    error,
    isLocked,
    isSendingCode,
    isValidating,
    resendCode,
    resendCooldown,
    validateCode,
  } = useOtpVerification({authToken, onVerified});

  const handleChange = (value: string) => {
    setCode(value);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCode('');
    validateCode(code);
  };

  const isCodeComplete = code.length === OTP_LENGTH;
  const showAttemptsHint = !isLocked && attemptsLeft < OTP_MAX_ATTEMPTS;

  return (
    <Container component='main' maxWidth='sm'>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          padding: 3,
          borderRadius: 3,
        }}
      >
        <Typography variant='h4' color='black' sx={{mb: 1}}>
          {t('title')}
        </Typography>
        <Typography variant='body1' color='black' sx={{mb: 3}}>
          {t('subtitle', {email})}
        </Typography>

        {isLocked ? (
          <>
            <Typography variant='body1' color='error' sx={{mb: 3}} data-testid='otp-locked-message'>
              {t('locked')}
            </Typography>
            <EmpionButton fullWidth onClick={onBackToSignIn}>
              {t('backToSignIn')}
            </EmpionButton>
          </>
        ) : (
          <Box component='form' onSubmit={handleSubmit} noValidate>
            <Typography variant='body1' color='black' component='label' id='otp-label'>
              {t('codeLabel')}
            </Typography>
            <MuiOtpInput
              autoFocus
              value={code}
              length={OTP_LENGTH}
              onChange={handleChange}
              validateChar={isDigit}
              aria-labelledby='otp-label'
              TextFieldsProps={{
                inputProps: {inputMode: 'numeric'},
                InputProps: {
                  sx: {
                    borderColor: orangeColors[5],
                    backgroundColor: orangeColors[7],
                    color: greenColors[700],
                  },
                },
              }}
              sx={{gap: 1, mt: 1}}
            />

            {error && (
              <Typography variant='body2' color='error' sx={{mt: 2}}>
                {t(error)}
              </Typography>
            )}
            {showAttemptsHint && (
              <Typography variant='body2' color='text.secondary' sx={{mt: 1}}>
                {t('attemptsRemaining', {count: attemptsLeft})}
              </Typography>
            )}
            {codeResent && (
              <Typography variant='body2' color='text.secondary' sx={{mt: 1}}>
                {t('resendSuccess')}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 3,
                mb: 2,
              }}
            >
              <EmpionButton type='submit' fullWidth disabled={!isCodeComplete || isValidating}>
                {t('submit')}
              </EmpionButton>
            </Box>

            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <EmpionButton
                variant='menu'
                size='small'
                type='button'
                disabled={resendCooldown > 0 || isSendingCode}
                onClick={resendCode}
              >
                {resendCooldown > 0 ? t('resendCooldown', {seconds: resendCooldown}) : t('resend')}
              </EmpionButton>
              <EmpionButton variant='menu' size='small' type='button' onClick={onBackToSignIn}>
                {t('backToSignIn')}
              </EmpionButton>
            </Box>
          </Box>
        )}
      </Box>
      <CopyrightLink sx={{mt: '8px'}} />
    </Container>
  );
};
